import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "TarjetaUID";

const STORAGE_EDICION =
  "tarjetaAsignacionEditar";

const ORIGEN_UID = Object.freeze({
  CELULAR_HEX: "CELULAR_HEX",
  LECTOR_DECIMAL: "LECTOR_DECIMAL",
});

const MAXIMO_UID_4_BYTES = 4294967295;

const LONGITUDES_UID_NFC = Object.freeze([
  8,
  14,
  20,
]);

/* =========================================================
   FUNCIONES AUXILIARES
========================================================= */

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();

const escaparHTML = (valor = "") => {
  const caracteres = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };

  return String(valor).replace(
    /[&<>"']/g,
    (caracter) => caracteres[caracter]
  );
};

const limpiarUIDHexadecimal = (valor = "") =>
  String(valor)
    .trim()
    .replace(/^0x/i, "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

const limpiarUIDDecimal = (valor = "") =>
  String(valor)
    .trim()
    .replace(/\D/g, "");

const detectarOrigenUID = (valor = "") => {
  const valorOriginal = String(valor).trim();

  const valorLimpio = valorOriginal
    .replace(/^0x/i, "")
    .replace(/[\s:-]/g, "")
    .toUpperCase();

  if (!valorLimpio) {
    return ORIGEN_UID.LECTOR_DECIMAL;
  }

  if (
    /^0x/i.test(valorOriginal) ||
    /[\s:-]/.test(valorOriginal) ||
    /[A-F]/.test(valorLimpio) ||
    valorLimpio.length > 10
  ) {
    return ORIGEN_UID.CELULAR_HEX;
  }

  return ORIGEN_UID.LECTOR_DECIMAL;
};

const limpiarEntradaUID = (valor, origen) => {
  if (origen === ORIGEN_UID.LECTOR_DECIMAL) {
    return limpiarUIDDecimal(valor);
  }

  return limpiarUIDHexadecimal(valor);
};

/**
 * Decimal USB -> UID normalizado.
 *
 * 3066319108
 * B6C45104
 * 0451C4B6
 */
const convertirDecimalLectorAHex = (valor) => {
  const texto = limpiarUIDDecimal(valor);

  if (!texto) {
    return "";
  }

  const numero = Number(texto);

  if (
    !Number.isSafeInteger(numero) ||
    numero < 0 ||
    numero > MAXIMO_UID_4_BYTES
  ) {
    throw new Error(
      "El código decimal debe estar entre 0 y 4294967295."
    );
  }

  const hexadecimalLector = numero
    .toString(16)
    .padStart(8, "0")
    .toUpperCase();

  const bytes = hexadecimalLector.match(/.{2}/g);

  if (!bytes || bytes.length !== 4) {
    throw new Error(
      "No fue posible convertir el código decimal."
    );
  }

  return [...bytes].reverse().join("");
};

/**
 * UID normalizado -> decimal USB.
 *
 * 0451C4B6
 * B6C45104
 * 3066319108
 */
const convertirHexADecimalLector = (
  uidNormalizado
) => {
  const uid =
    limpiarUIDHexadecimal(uidNormalizado);

  if (!/^[0-9A-F]{8}$/.test(uid)) {
    throw new Error(
      "El UID normalizado debe tener ocho caracteres hexadecimales."
    );
  }

  const bytes = uid.match(/.{2}/g);

  if (!bytes || bytes.length !== 4) {
    throw new Error(
      "No fue posible convertir el UID al código decimal."
    );
  }

  const hexadecimalLector = [...bytes]
    .reverse()
    .join("");

  const numeroDecimal = Number.parseInt(
    hexadecimalLector,
    16
  );

  if (
    !Number.isSafeInteger(numeroDecimal) ||
    numeroDecimal < 0 ||
    numeroDecimal > MAXIMO_UID_4_BYTES
  ) {
    throw new Error(
      "No fue posible calcular el código decimal."
    );
  }

  return String(numeroDecimal);
};

const validarUID = (valor, origen) => {
  try {
    const valorLimpio = limpiarEntradaUID(
      valor,
      origen
    );

    if (!valorLimpio) {
      return false;
    }

    if (origen === ORIGEN_UID.LECTOR_DECIMAL) {
      const numero = Number(valorLimpio);

      return (
        /^\d+$/.test(valorLimpio) &&
        Number.isSafeInteger(numero) &&
        numero >= 0 &&
        numero <= MAXIMO_UID_4_BYTES
      );
    }

    return (
      /^[0-9A-F]+$/.test(valorLimpio) &&
      LONGITUDES_UID_NFC.includes(
        valorLimpio.length
      )
    );
  } catch (error) {
    return false;
  }
};

const obtenerCodigosTarjeta = (
  valor,
  origen
) => {
  const valorLimpio = limpiarEntradaUID(
    valor,
    origen
  );

  if (origen === ORIGEN_UID.LECTOR_DECIMAL) {
    const uidLectorDecimal =
      limpiarUIDDecimal(valorLimpio);

    const uidNormalizado =
      convertirDecimalLectorAHex(
        uidLectorDecimal
      );

    return {
      uidRecibido: uidLectorDecimal,
      uidCompleto: "",
      uidNormalizado,
      uidLectorDecimal,
    };
  }

  const uidCompleto =
    limpiarUIDHexadecimal(valorLimpio);

  if (
    !LONGITUDES_UID_NFC.includes(
      uidCompleto.length
    )
  ) {
    throw new Error(
      "El UID NFC debe tener 4, 7 o 10 bytes."
    );
  }

  const uidNormalizado =
    uidCompleto.slice(0, 8);

  const uidLectorDecimal =
    convertirHexADecimalLector(
      uidNormalizado
    );

  return {
    uidRecibido: uidCompleto,
    uidCompleto,
    uidNormalizado,
    uidLectorDecimal,
  };
};

const obtenerNombreOrigen = (origen) =>
  origen === ORIGEN_UID.CELULAR_HEX
    ? "NFC del celular"
    : "Lector USB";

const obtenerNombreCompleto = (
  aprendiz = {}
) =>
  [
    aprendiz.nombre1Aprendiz,
    aprendiz.nombre2Aprendiz,
    aprendiz.apellido1Aprendiz,
    aprendiz.apellido2Aprendiz,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const obtenerPermisosModulo = () => {
  try {
    const permisosStorage =
      localStorage.getItem("permisos");

    const usuarioStorage =
      localStorage.getItem("usuario");

    const userStorage =
      localStorage.getItem("user");

    const usuariosStorage =
      localStorage.getItem("usuarios");

    const permisosData = permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};

    const modulos = Array.isArray(permisosData)
      ? permisosData
      : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

    return (
      modulos.find((modulo) => {
        const nombreModulo =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          "";

        return (
          normalizarTexto(nombreModulo) ===
          normalizarTexto(MODULO)
        );
      }) || {}
    );
  } catch (error) {
    console.error(
      "Error leyendo los permisos:",
      error
    );

    return {};
  }
};

/* =========================================================
   COMPONENTE
========================================================= */

const RegistrarTarjetaAprendiz = () => {
  /**
   * Este id es el identificador de la asignación.
   * No es el documento del aprendiz.
   */
  const { id } = useParams();

  const navigate = useNavigate();

  const esEdicion = Boolean(id);

  const [
    documentoAprendiz,
    setDocumentoAprendiz,
  ] = useState("");

  const [
    nombreAprendiz,
    setNombreAprendiz,
  ] = useState("");

  const [
    aprendizId,
    setAprendizId,
  ] = useState("");

  const [
    tarjetaUID,
    setTarjetaUID,
  ] = useState("");

  const [
    origenUID,
    setOrigenUID,
  ] = useState(
    ORIGEN_UID.LECTOR_DECIMAL
  );

  const [
    cargandoRegistro,
    setCargandoRegistro,
  ] = useState(false);

  const [
    verificando,
    setVerificando,
  ] = useState(false);

  const [
    guardando,
    setGuardando,
  ] = useState(false);

  const [
    leyendoNFC,
    setLeyendoNFC,
  ] = useState(false);

  const lectorNFCRef = useRef(null);

  const abortControllerRef = useRef(null);

  const lecturaProcesadaRef = useRef(false);

  /* =======================================================
     PERMISOS
  ======================================================= */

  const permisosModulo = useMemo(
    () => obtenerPermisosModulo(),
    []
  );

  const puedeCrear =
    permisosModulo?.puedeCrear === true ||
    permisosModulo?.crear === true;

  const puedeActualizar =
    permisosModulo?.puedeActualizar === true ||
    permisosModulo?.actualizar === true ||
    permisosModulo?.editar === true;

  const puedeGuardar = esEdicion
    ? puedeActualizar
    : puedeCrear;

  /* =======================================================
     VALORES SEGUROS
  ======================================================= */

  const documentoAprendizTexto = useMemo(
    () =>
      String(
        documentoAprendiz ?? ""
      ),
    [documentoAprendiz]
  );

  const documentoAprendizValido = useMemo(
    () =>
      Boolean(
        documentoAprendizTexto.trim()
      ),
    [documentoAprendizTexto]
  );

  const aprendizVerificado = useMemo(
    () =>
      Boolean(
        aprendizId &&
        nombreAprendiz
      ),
    [
      aprendizId,
      nombreAprendiz,
    ]
  );

  const nfcDisponible = useMemo(
    () =>
      typeof window !== "undefined" &&
      "NDEFReader" in window,
    []
  );

  const contextoSeguro = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.isSecureContext,
    []
  );

  const uidValido = useMemo(
    () =>
      validarUID(
        tarjetaUID,
        origenUID
      ),
    [
      tarjetaUID,
      origenUID,
    ]
  );

  const codigosVista = useMemo(() => {
    if (!uidValido) {
      return {
        uidCompleto: "",
        uidNormalizado: "",
        uidLectorDecimal: "",
      };
    }

    try {
      return obtenerCodigosTarjeta(
        tarjetaUID,
        origenUID
      );
    } catch (error) {
      return {
        uidCompleto: "",
        uidNormalizado: "",
        uidLectorDecimal: "",
      };
    }
  }, [
    tarjetaUID,
    origenUID,
    uidValido,
  ]);

  /* =======================================================
     DETENER NFC
  ======================================================= */

  const detenerLecturaNFC = useCallback(() => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    } catch (error) {
      console.error(
        "Error deteniendo NFC:",
        error
      );
    } finally {
      abortControllerRef.current = null;
      lectorNFCRef.current = null;
      lecturaProcesadaRef.current = false;

      setLeyendoNFC(false);
    }
  }, []);

  /* =======================================================
     APLICAR INFORMACIÓN CONSULTADA
  ======================================================= */

  const aplicarAsignacion = useCallback(
    (asignacion) => {
      if (!asignacion?._id) {
        throw new Error(
          "La información de la asignación no es válida."
        );
      }

      /**
       * El backend debe entregar aprendizObjectId
       * poblado mediante populate.
       */
      const aprendiz =
        asignacion.aprendizObjectId &&
        typeof asignacion.aprendizObjectId ===
          "object"
          ? asignacion.aprendizObjectId
          : null;

      if (!aprendiz?._id) {
        throw new Error(
          "La asignación no contiene la información completa del aprendiz."
        );
      }

      setAprendizId(
        String(aprendiz._id)
      );

      setDocumentoAprendiz(
        String(
          aprendiz.documentoAprendiz ?? ""
        )
      );

      setNombreAprendiz(
        obtenerNombreCompleto(aprendiz)
      );

      /**
       * Se prioriza el UID completo.
       */
      if (
        asignacion.tarjetaUIDCompleto
      ) {
        setTarjetaUID(
          String(
            asignacion.tarjetaUIDCompleto
          )
        );

        setOrigenUID(
          ORIGEN_UID.CELULAR_HEX
        );

        return;
      }

      /**
       * Si no tiene UID completo, utiliza
       * el código decimal almacenado.
       */
      if (
        asignacion
          .tarjetaUIDLectorDecimal
      ) {
        setTarjetaUID(
          String(
            asignacion
              .tarjetaUIDLectorDecimal
          )
        );

        setOrigenUID(
          ORIGEN_UID.LECTOR_DECIMAL
        );

        return;
      }

      /**
       * Compatibilidad con registros antiguos.
       */
      setTarjetaUID(
        String(
          asignacion.tarjetaUID ?? ""
        )
      );

      setOrigenUID(
        ORIGEN_UID.CELULAR_HEX
      );
    },
    []
  );

  /* =======================================================
     GET DATA DE LA ASIGNACIÓN
  ======================================================= */

  const getData = useCallback(
    async (forzarConsulta = false) => {
      if (!esEdicion || !id) {
        return;
      }

      try {
        setCargandoRegistro(true);

        detenerLecturaNFC();

        let asignacion = null;

        /**
         * Primero revisa localStorage.
         */
        if (!forzarConsulta) {
          const informacionLocal =
            localStorage.getItem(
              STORAGE_EDICION
            );

          if (informacionLocal) {
            try {
              const asignacionLocal =
                JSON.parse(
                  informacionLocal
                );

              if (
                String(
                  asignacionLocal?._id
                ) === String(id)
              ) {
                asignacion =
                  asignacionLocal;
              }
            } catch (error) {
              console.error(
                "Información local no válida:",
                error
              );

              localStorage.removeItem(
                STORAGE_EDICION
              );
            }
          }
        }

        /**
         * Cuando no existe localmente,
         * consulta el backend mediante el
         * ID de la asignación.
         */
        if (!asignacion) {
          const response = await axios.get(
            `/api/tarjetas/listid/${id}`,
            authConfig()
          );

          asignacion =
            response.data?.asignacion;

          if (!asignacion?._id) {
            throw new Error(
              "No se recibió la información de la asignación."
            );
          }

          localStorage.setItem(
            STORAGE_EDICION,
            JSON.stringify(asignacion)
          );
        }

        aplicarAsignacion(asignacion);
      } catch (error) {
        console.error(
          "Error cargando la asignación:",
          error
        );

        localStorage.removeItem(
          STORAGE_EDICION
        );

        await Swal.fire({
          icon: "error",
          title:
            "No se pudo cargar la asignación",
          text:
            error.response?.data?.message ||
            error.message ||
            "No fue posible consultar la información.",
          confirmButtonText: "Aceptar",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        navigate(
          "/tarjeta/listartarjetaaprendiz"
        );
      } finally {
        setCargandoRegistro(false);
      }
    },
    [
      aplicarAsignacion,
      detenerLecturaNFC,
      esEdicion,
      id,
      navigate,
    ]
  );

  /* =======================================================
     EFECTOS
  ======================================================= */

  useEffect(() => {
    if (esEdicion) {
      getData();
    }
  }, [
    esEdicion,
    getData,
  ]);

  useEffect(() => {
    return () => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      } catch (error) {
        console.error(
          "Error cerrando lector NFC:",
          error
        );
      }
    };
  }, []);

  /* =======================================================
     LIMPIAR Y REGRESAR
  ======================================================= */

  const limpiarFormulario =
    useCallback(async () => {
      detenerLecturaNFC();

      if (esEdicion) {
        await getData(true);
        return;
      }

      setDocumentoAprendiz("");
      setNombreAprendiz("");
      setAprendizId("");
      setTarjetaUID("");

      setOrigenUID(
        ORIGEN_UID.LECTOR_DECIMAL
      );
    }, [
      detenerLecturaNFC,
      esEdicion,
      getData,
    ]);

  const regresarListado = useCallback(() => {
    detenerLecturaNFC();

    localStorage.removeItem(
      STORAGE_EDICION
    );

    navigate(
      "/tarjeta/listartarjetaaprendiz"
    );
  }, [
    detenerLecturaNFC,
    navigate,
  ]);

  /* =======================================================
     VERIFICAR APRENDIZ
  ======================================================= */

  const verificarDocumentoAprendiz =
    useCallback(
      async (documento) => {
        const documentoTexto = String(
          documento ?? ""
        ).trim();

        if (
          !documentoTexto ||
          verificando ||
          guardando ||
          cargandoRegistro
        ) {
          return;
        }

        detenerLecturaNFC();

        try {
          setVerificando(true);

          const response = await axios.get(
            `/api/aprendices/verificar/${encodeURIComponent(
              documentoTexto
            )}`,
            authConfig()
          );

          if (!response.data?.exists) {
            setNombreAprendiz("");
            setAprendizId("");

            await Swal.fire({
              icon: "error",
              title:
                "Aprendiz no encontrado",
              text:
                "El documento ingresado no corresponde a ningún aprendiz.",
              confirmButtonText: "Aceptar",
            });

            return;
          }

          const nombreCompleto =
            response.data?.nombreCompleto ||
            "";

          const idEncontrado =
            response.data?.aprendizId ||
            "";

          if (!idEncontrado) {
            setNombreAprendiz("");
            setAprendizId("");

            await Swal.fire({
              icon: "warning",
              title:
                "Información incompleta",
              text:
                "El aprendiz fue encontrado, pero no se recibió su identificador.",
              confirmButtonText: "Aceptar",
            });

            return;
          }

          setNombreAprendiz(
            String(nombreCompleto)
          );

          setAprendizId(
            String(idEncontrado)
          );

          await Swal.fire({
            icon: "success",
            title:
              "Aprendiz verificado",
            text: String(nombreCompleto),
            confirmButtonText: "Aceptar",
          });
        } catch (error) {
          console.error(
            "Error verificando aprendiz:",
            error
          );

          setNombreAprendiz("");
          setAprendizId("");

          await Swal.fire({
            icon: "error",
            title:
              "Error al verificar",
            text:
              error.response?.data?.message ||
              "No se pudo verificar el documento del aprendiz.",
            confirmButtonText: "Aceptar",
          });
        } finally {
          setVerificando(false);
        }
      },
      [
        cargandoRegistro,
        detenerLecturaNFC,
        guardando,
        verificando,
      ]
    );

  /* =======================================================
     INICIAR LECTURA NFC
  ======================================================= */

  const iniciarLecturaNFC =
    useCallback(async () => {
      if (!puedeGuardar) {
        await Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text: esEdicion
            ? "No tiene permisos para actualizar asignaciones."
            : "No tiene permisos para asignar tarjetas.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!aprendizVerificado) {
        await Swal.fire({
          icon: "warning",
          title:
            "Aprendiz sin verificar",
          text:
            "Primero debe verificar el documento del aprendiz.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!nfcDisponible) {
        await Swal.fire({
          icon: "warning",
          title: "NFC no disponible",
          html: `
            <p>
              Este navegador no permite utilizar Web NFC.
            </p>

            <p class="mb-0">
              Abra la aplicación desde
              <strong>Google Chrome en Android</strong>
              y verifique que el celular tenga NFC.
            </p>
          `,
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!contextoSeguro) {
        await Swal.fire({
          icon: "warning",
          title: "Se requiere HTTPS",
          text:
            "La lectura NFC solamente funciona desde una conexión segura.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      try {
        detenerLecturaNFC();

        const abortController =
          new AbortController();

        const lector =
          new window.NDEFReader();

        abortControllerRef.current =
          abortController;

        lectorNFCRef.current = lector;

        lecturaProcesadaRef.current = false;

        lector.addEventListener(
          "reading",
          async (evento) => {
            if (
              lecturaProcesadaRef.current
            ) {
              return;
            }

            lecturaProcesadaRef.current = true;

            const uidCompleto =
              limpiarUIDHexadecimal(
                evento.serialNumber || ""
              );

            if (!uidCompleto) {
              detenerLecturaNFC();

              await Swal.fire({
                icon: "warning",
                title:
                  "UID no disponible",
                text:
                  "La tarjeta fue detectada, pero el navegador no entregó su UID.",
                confirmButtonText: "Aceptar",
              });

              return;
            }

            if (
              !LONGITUDES_UID_NFC.includes(
                uidCompleto.length
              )
            ) {
              detenerLecturaNFC();

              await Swal.fire({
                icon: "warning",
                title:
                  "UID NFC no válido",
                text:
                  "El UID leído debe contener 4, 7 o 10 bytes.",
                confirmButtonText: "Aceptar",
              });

              return;
            }

            let codigos;

            try {
              codigos =
                obtenerCodigosTarjeta(
                  uidCompleto,
                  ORIGEN_UID.CELULAR_HEX
                );
            } catch (error) {
              detenerLecturaNFC();

              await Swal.fire({
                icon: "error",
                title:
                  "Error procesando el UID",
                text:
                  error.message ||
                  "No fue posible procesar el UID.",
                confirmButtonText: "Aceptar",
              });

              return;
            }

            setTarjetaUID(uidCompleto);

            setOrigenUID(
              ORIGEN_UID.CELULAR_HEX
            );

            detenerLecturaNFC();

            await Swal.fire({
              icon: "success",
              title:
                "Tarjeta detectada",
              html: `
                <div class="text-start">
                  <div class="mb-2">
                    <strong>UID completo:</strong>
                    <span class="font-monospace">
                      ${escaparHTML(
                        codigos.uidCompleto
                      )}
                    </span>
                  </div>

                  <div class="mb-2">
                    <strong>UID normalizado:</strong>
                    <span class="font-monospace">
                      ${escaparHTML(
                        codigos.uidNormalizado
                      )}
                    </span>
                  </div>

                  <div>
                    <strong>Código USB:</strong>
                    <span class="font-monospace">
                      ${escaparHTML(
                        codigos.uidLectorDecimal
                      )}
                    </span>
                  </div>
                </div>
              `,
              confirmButtonText: "Aceptar",
            });
          }
        );

        lector.addEventListener(
          "readingerror",
          async () => {
            detenerLecturaNFC();

            await Swal.fire({
              icon: "error",
              title:
                "Error de lectura",
              text:
                "La tarjeta fue detectada, pero el navegador no pudo procesarla.",
              confirmButtonText: "Aceptar",
            });
          }
        );

        setLeyendoNFC(true);

        await lector.scan({
          signal:
            abortController.signal,
        });

        await Swal.fire({
          icon: "info",
          title:
            "Lector NFC activo",
          html: `
            <p>
              Acerque la tarjeta a la parte posterior del celular.
            </p>

            <p class="mb-0">
              La tarjeta no se guardará automáticamente.
            </p>
          `,
          confirmButtonText: "Entendido",
        });
      } catch (error) {
        if (
          error?.name ===
          "AbortError"
        ) {
          return;
        }

        console.error(
          "Error iniciando NFC:",
          error
        );

        detenerLecturaNFC();

        let mensaje =
          "No se pudo iniciar el lector NFC.";

        switch (error?.name) {
          case "NotAllowedError":
            mensaje =
              "El permiso NFC fue rechazado.";
            break;

          case "NotSupportedError":
            mensaje =
              "El dispositivo o navegador no permite esta lectura NFC.";
            break;

          case "NotReadableError":
            mensaje =
              "El NFC está ocupado por otra aplicación.";
            break;

          case "InvalidStateError":
            mensaje =
              "Ya existe una lectura NFC activa.";
            break;

          case "SecurityError":
            mensaje =
              "La lectura NFC requiere HTTPS.";
            break;

          default:
            mensaje =
              error?.message ||
              mensaje;
            break;
        }

        await Swal.fire({
          icon: "error",
          title: "Error NFC",
          text: mensaje,
          confirmButtonText: "Aceptar",
        });
      }
    }, [
      aprendizVerificado,
      contextoSeguro,
      detenerLecturaNFC,
      esEdicion,
      nfcDisponible,
      puedeGuardar,
    ]);

  /* =======================================================
     GUARDAR O ACTUALIZAR
  ======================================================= */

  const guardarTarjeta =
    useCallback(async () => {
      if (!puedeGuardar) {
        await Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text: esEdicion
            ? "No tiene permisos para actualizar asignaciones."
            : "No tiene permisos para asignar tarjetas.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!aprendizId) {
        await Swal.fire({
          icon: "warning",
          title:
            "Aprendiz sin verificar",
          text:
            "Debe verificar el documento del aprendiz.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      const uidOriginal =
        limpiarEntradaUID(
          tarjetaUID,
          origenUID
        );

      if (!uidOriginal) {
        await Swal.fire({
          icon: "warning",
          title: "UID requerido",
          text:
            "Debe escribir o leer el UID de la tarjeta.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (
        !validarUID(
          uidOriginal,
          origenUID
        )
      ) {
        await Swal.fire({
          icon: "warning",
          title: "UID no válido",
          text:
            origenUID ===
            ORIGEN_UID.LECTOR_DECIMAL
              ? "El código decimal debe estar entre 0 y 4294967295."
              : "El UID NFC debe contener exactamente 4, 7 o 10 bytes.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      let codigos;

      try {
        codigos =
          obtenerCodigosTarjeta(
            uidOriginal,
            origenUID
          );
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: "Error en el UID",
          text: error.message,
          confirmButtonText: "Aceptar",
        });

        return;
      }

      detenerLecturaNFC();

      const confirmacion =
        await Swal.fire({
          icon: "question",

          title: esEdicion
            ? "Confirmar actualización"
            : "Confirmar asignación",

          html: `
            <div class="text-start">
              <p>
                ${
                  esEdicion
                    ? "Revise la información antes de actualizar la asignación."
                    : "Revise la información antes de asignar la tarjeta."
                }
              </p>

              <div class="mb-2">
                <strong>Aprendiz:</strong>
                ${escaparHTML(
                  nombreAprendiz
                )}
              </div>

              <div class="mb-2">
                <strong>Origen:</strong>
                ${escaparHTML(
                  obtenerNombreOrigen(
                    origenUID
                  )
                )}
              </div>

              ${
                codigos.uidCompleto
                  ? `
                    <div class="mb-2">
                      <strong>UID completo:</strong>
                      <span class="font-monospace">
                        ${escaparHTML(
                          codigos.uidCompleto
                        )}
                      </span>
                    </div>
                  `
                  : ""
              }

              <div class="mb-2">
                <strong>UID normalizado:</strong>
                <span class="font-monospace">
                  ${escaparHTML(
                    codigos.uidNormalizado
                  )}
                </span>
              </div>

              <div>
                <strong>Código USB:</strong>
                <span class="font-monospace">
                  ${escaparHTML(
                    codigos.uidLectorDecimal
                  )}
                </span>
              </div>
            </div>
          `,

          showCancelButton: true,

          confirmButtonText: esEdicion
            ? "Sí, actualizar"
            : "Sí, asignar",

          cancelButtonText: "Cancelar",

          confirmButtonColor: esEdicion
            ? "#ffc107"
            : "#212529",

          cancelButtonColor: "#6c757d",

          reverseButtons: true,
          focusCancel: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

      if (!confirmacion.isConfirmed) {
        return;
      }

      try {
        setGuardando(true);

        const datosAsignacion = {
          aprendizObjectId:
            String(aprendizId),

          tarjetaUID:
            uidOriginal,

          origenUID,
        };

        let response;

        if (esEdicion) {
          response = await axios.put(
            `/api/tarjetas/update/${id}`,
            datosAsignacion,
            authConfig()
          );
        } else {
          response = await axios.post(
            "/api/tarjetas/add",
            datosAsignacion,
            authConfig()
          );
        }

        localStorage.removeItem(
          STORAGE_EDICION
        );

        await Swal.fire({
          icon: "success",

          title: esEdicion
            ? "Asignación actualizada"
            : "Tarjeta asignada",

          text:
            response.data?.message ||
            (esEdicion
              ? "La asignación fue actualizada correctamente."
              : "La tarjeta fue asignada correctamente."),

          confirmButtonText: "Aceptar",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        navigate(
          "/tarjeta/listartarjetaaprendiz"
        );
      } catch (error) {
        console.error(
          "Error guardando la tarjeta:",
          error
        );

        await Swal.fire({
          icon: "error",

          title: esEdicion
            ? "No se pudo actualizar"
            : "No se pudo asignar",

          text:
            error.response?.data?.message ||
            error.response?.data?.error ||
            "No se pudo guardar la asignación.",

          confirmButtonText: "Aceptar",
        });
      } finally {
        setGuardando(false);
      }
    }, [
      aprendizId,
      detenerLecturaNFC,
      esEdicion,
      id,
      navigate,
      nombreAprendiz,
      origenUID,
      puedeGuardar,
      tarjetaUID,
    ]);

  /* =======================================================
     EVENTOS DEL FORMULARIO
  ======================================================= */

  const handleSubmit = async (evento) => {
    evento.preventDefault();

    await guardarTarjeta();
  };

  const handleDocumentoChange = (evento) => {
    detenerLecturaNFC();

    setDocumentoAprendiz(
      String(
        evento.target.value ?? ""
      )
    );

    setNombreAprendiz("");
    setAprendizId("");
  };

  const handleDocumentoKeyDown = (evento) => {
    if (evento.key === "Enter") {
      evento.preventDefault();

      verificarDocumentoAprendiz(
        documentoAprendizTexto
      );
    }
  };

  const handleDocumentoBlur = () => {
    const documento =
      documentoAprendizTexto.trim();

    if (
      documento &&
      !aprendizId &&
      !verificando
    ) {
      verificarDocumentoAprendiz(
        documento
      );
    }
  };

  const handleTarjetaUIDChange = (evento) => {
    const valorIngresado =
      evento.target.value;

    const origenDetectado =
      detectarOrigenUID(
        valorIngresado
      );

    const valorLimpio =
      limpiarEntradaUID(
        valorIngresado,
        origenDetectado
      );

    setOrigenUID(
      origenDetectado
    );

    setTarjetaUID(
      valorLimpio
    );
  };

  const procesoActivo =
    cargandoRegistro ||
    verificando ||
    guardando;

  const formularioDeshabilitado =
    procesoActivo ||
    !puedeGuardar;

  /* =======================================================
     INTERFAZ
  ======================================================= */

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Encabezado */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {esEdicion
                    ? "Editar asignación de tarjeta"
                    : "Asignar tarjeta"}
                </h5>

                <small className="text-secondary">
                  {esEdicion
                    ? "Modifique el aprendiz o la tarjeta y confirme los cambios."
                    : "Verifique el aprendiz, lea o escriba el UID y confirme la asignación."}
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={regresarListado}
                  disabled={procesoActivo}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2" />
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={limpiarFormulario}
                  disabled={
                    formularioDeshabilitado
                  }
                >
                  <i
                    className={`fa-solid ${
                      esEdicion
                        ? "fa-rotate-left"
                        : "fa-eraser"
                    } me-2`}
                  />

                  {esEdicion
                    ? "Restablecer"
                    : "Limpiar"}
                </button>
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-3 p-md-4">
            {!puedeGuardar && (
              <div className="alert alert-warning">
                <i className="fa-solid fa-triangle-exclamation me-2" />

                {esEdicion
                  ? "No tiene permisos para actualizar asignaciones."
                  : "No tiene permisos para asignar tarjetas."}
              </div>
            )}

            {!nfcDisponible && (
              <div className="alert alert-info">
                <i className="fa-solid fa-circle-info me-2" />

                <strong>
                  Web NFC no está disponible.
                </strong>

                <div className="mt-1">
                  Puede utilizar el lector USB o escribir manualmente el código.
                </div>
              </div>
            )}

            {nfcDisponible &&
              !contextoSeguro && (
                <div className="alert alert-warning">
                  <i className="fa-solid fa-lock me-2" />
                  La lectura NFC requiere HTTPS.
                </div>
              )}

            {procesoActivo && (
              <div className="text-center text-secondary py-3">
                <div
                  className="spinner-border"
                  role="status"
                  aria-label="Procesando"
                />

                <div className="mt-2">
                  {cargandoRegistro
                    ? "Cargando asignación..."
                    : verificando
                      ? "Verificando aprendiz..."
                      : esEdicion
                        ? "Actualizando asignación..."
                        : "Asignando tarjeta..."}
                </div>
              </div>
            )}

            {leyendoNFC && (
              <div className="alert alert-primary">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <i className="fa-solid fa-wifi me-2" />

                    <strong>
                      Lector NFC activo.
                    </strong>

                    <div className="mt-1">
                      Acerque la tarjeta a la parte posterior del celular.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={
                      detenerLecturaNFC
                    }
                  >
                    <i className="fa-solid fa-circle-stop me-2" />
                    Detener
                  </button>
                </div>
              </div>
            )}

            <form
              id="form-tarjeta"
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="row g-3 align-items-end">
                {/* Documento */}
                <div className="col-12 col-md-3">
                  <label
                    htmlFor="documentoAprendiz"
                    className="form-label fw-semibold"
                  >
                    Documento aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-id-card" />
                    </span>

                    <input
                      id="documentoAprendiz"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="form-control"
                      placeholder="Ej: 123456789"
                      value={
                        documentoAprendizTexto
                      }
                      onChange={
                        handleDocumentoChange
                      }
                      onBlur={
                        handleDocumentoBlur
                      }
                      onKeyDown={
                        handleDocumentoKeyDown
                      }
                      required
                      disabled={
                        formularioDeshabilitado
                      }
                    />

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        verificarDocumentoAprendiz(
                          documentoAprendizTexto
                        )
                      }
                      disabled={
                        formularioDeshabilitado ||
                        !documentoAprendizValido
                      }
                    >
                      <i
                        className={`fa-solid ${
                          verificando
                            ? "fa-spinner fa-spin"
                            : "fa-magnifying-glass"
                        } me-2`}
                      />

                      Verificar
                    </button>
                  </div>

                  <div className="form-text">
                    Presione Enter o utilice el botón Verificar.
                  </div>
                </div>

                {/* Nombre */}
                <div className="col-12 col-md-4">
                  <label
                    htmlFor="nombreAprendiz"
                    className="form-label fw-semibold"
                  >
                    Nombre aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-user" />
                    </span>

                    <input
                      id="nombreAprendiz"
                      type="text"
                      className="form-control"
                      placeholder="Se completa automáticamente"
                      value={nombreAprendiz}
                      readOnly
                      disabled
                    />

                    <span
                      className={`input-group-text ${
                        aprendizVerificado
                          ? "text-success"
                          : "text-secondary"
                      }`}
                    >
                      <i
                        className={`fa-solid ${
                          aprendizVerificado
                            ? "fa-circle-check"
                            : "fa-circle-info"
                        }`}
                      />
                    </span>
                  </div>

                  <div className="form-text">
                    {aprendizVerificado
                      ? "Aprendiz verificado correctamente."
                      : "Primero verifique el documento."}
                  </div>
                </div>

                {/* Tarjeta */}
                <div className="col-12 col-md-5">
                  <label
                    htmlFor="tarjetaUID"
                    className="form-label fw-semibold"
                  >
                    UID de la tarjeta
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-solid fa-id-badge" />
                    </span>

                    <input
                      id="tarjetaUID"
                      type="text"
                      autoComplete="off"
                      className="form-control text-uppercase"
                      placeholder="Lea o escriba el código"
                      value={tarjetaUID}
                      onChange={
                        handleTarjetaUIDChange
                      }
                      required
                      disabled={
                        formularioDeshabilitado ||
                        !aprendizVerificado ||
                        leyendoNFC
                      }
                    />

                    <button
                      type="button"
                      className={
                        leyendoNFC
                          ? "btn btn-outline-danger"
                          : "btn btn-outline-primary"
                      }
                      onClick={
                        leyendoNFC
                          ? detenerLecturaNFC
                          : iniciarLecturaNFC
                      }
                      disabled={
                        formularioDeshabilitado ||
                        !aprendizVerificado ||
                        (!nfcDisponible &&
                          !leyendoNFC)
                      }
                    >
                      <i
                        className={`fa-solid ${
                          leyendoNFC
                            ? "fa-circle-stop"
                            : "fa-wifi"
                        } me-2`}
                      />

                      {leyendoNFC
                        ? "Detener"
                        : "Leer NFC"}
                    </button>
                  </div>

                  <div className="form-text">
                    Entrada detectada como{" "}
                    <strong>
                      {obtenerNombreOrigen(
                        origenUID
                      )}
                    </strong>
                    .
                  </div>
                </div>

                {/* Vista previa */}
                {tarjetaUID && (
                  <div className="col-12">
                    <div
                      className={`alert ${
                        uidValido
                          ? "alert-success"
                          : "alert-warning"
                      } mb-0`}
                    >
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        <div>
                          <strong>
                            Origen:
                          </strong>{" "}
                          {obtenerNombreOrigen(
                            origenUID
                          )}
                        </div>

                        <div>
                          <strong>
                            Código recibido:
                          </strong>{" "}
                          <span className="font-monospace">
                            {tarjetaUID}
                          </span>
                        </div>

                        {codigosVista.uidCompleto && (
                          <div>
                            <strong>
                              UID completo:
                            </strong>{" "}
                            <span className="font-monospace">
                              {
                                codigosVista.uidCompleto
                              }
                            </span>
                          </div>
                        )}

                        {codigosVista.uidNormalizado && (
                          <div>
                            <strong>
                              UID normalizado:
                            </strong>{" "}
                            <span className="font-monospace">
                              {
                                codigosVista.uidNormalizado
                              }
                            </span>
                          </div>
                        )}

                        {codigosVista.uidLectorDecimal && (
                          <div>
                            <strong>
                              Código USB:
                            </strong>{" "}
                            <span className="font-monospace">
                              {
                                codigosVista.uidLectorDecimal
                              }
                            </span>
                          </div>
                        )}
                      </div>

                      {!uidValido && (
                        <div className="mt-2">
                          El código ingresado no tiene un formato válido.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Botones */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={regresarListado}
                      disabled={procesoActivo}
                    >
                      <i className="fa-solid fa-xmark me-2" />
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      className={
                        esEdicion
                          ? "btn btn-warning text-dark"
                          : "btn btn-dark"
                      }
                      disabled={
                        formularioDeshabilitado ||
                        leyendoNFC ||
                        !aprendizVerificado ||
                        !uidValido
                      }
                    >
                      <i
                        className={`fa-solid ${
                          guardando
                            ? "fa-spinner fa-spin"
                            : esEdicion
                              ? "fa-floppy-disk"
                              : "fa-link"
                        } me-2`}
                      />

                      {guardando
                        ? esEdicion
                          ? "Actualizando..."
                          : "Asignando..."
                        : esEdicion
                          ? "Actualizar asignación"
                          : "Asignar tarjeta"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <hr className="my-4" />

            <div className="small text-secondary">
              <i className="fa-solid fa-circle-info me-2" />

              El parámetro de la URL corresponde al ID de la asignación. La información del aprendiz se obtiene mediante la consulta al backend.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarTarjetaAprendiz;